/**
 * FairwayBid – Impressum
 *
 * Die Angaben stehen hier und nicht in der Console, weil sie an genau einer
 * Stelle stimmen müssen und weil sie eine feste Form haben: Betreiberin,
 * Anschrift, Kontakt, Register, Umsatzsteuer. Als Fließtext in der Console
 * lässt sich das nicht gestalten und geht beim nächsten Bearbeiten kaputt.
 *
 * Nicht ausgefüllte Angaben (null) werden auf der Seite weggelassen und
 * unten in einem Hinweis benannt – lieber eine sichtbare Lücke als eine
 * erfundene Registernummer.
 */

export const IMPRINT_PAGE_ID = 'imprint';
export const IMPRINT_TYPE = 'fwb-impressum';

/**
 * Betreiberin der Plattform.
 *
 * ACHTUNG, zwei offene Punkte:
 *
 * 1. Die Anschrift ist die der Unternehmensgruppe (bisher im Impressum der
 *    Vito's Sporting Goods GmbH). Ob die Clubhouse Holding GmbH dieselbe
 *    Geschäftsanschrift führt, ist zu prüfen – im Impressum muss die
 *    Anschrift der genannten Gesellschaft stehen.
 *
 * 2. Registergericht, Registernummer und USt-IdNr. der Clubhouse Holding
 *    GmbH sind nicht bekannt. Die alten Werte (Amtsgericht Friedberg,
 *    HRB 10554, DE368529023) gehören der Vito's Sporting Goods GmbH und
 *    dürfen hier nicht stehen – sie sind deshalb bewusst leer.
 */
export const BETREIBER = {
  name: 'Clubhouse Holding GmbH',
  strasse: 'Mozartstraße 8',
  plzOrt: '61200 Wölfersheim',
  land: 'Deutschland',
};

/**
 * ============================================================
 *  HIER EINTRAGEN – die drei offenen Angaben
 * ============================================================
 *
 * Register und Registernummer stehen inzwischen (Quelle: die Vorstart-Seite
 * auf www.fairwaybid.com). Offen sind noch die drei Werte hier – Tatsachen,
 * die sich nicht herleiten lassen; erfundene wären schlimmer als fehlende. Solange sie null sind, lässt die Seite die Zeile weg und nennt
 * die Lücke unten ausdrücklich.
 *
 * Wo die Werte stehen:
 *
 *   geschaeftsfuehrer  Im Handelsregisterauszug der Clubhouse Holding GmbH
 *                      unter "Vertretungsberechtigte". Mehrere durch Komma.
 *   telefon            Eine erreichbare Nummer. § 5 DDG verlangt neben der
 *                      E-Mail einen zweiten Weg für schnellen Kontakt.
 *   UST_ID             Der Bescheid des Bundeszentralamts für Steuern,
 *                      Form "DE" plus neun Ziffern. Gibt es für die Holding
 *                      keine, bleibt der Wert null – dann entfällt die
 *                      Zeile zu Recht.
 *
 * Ein Auszug kostet beim Registerportal der Länder ein paar Euro und ist in
 * Minuten da: handelsregister.de → Normale Registerauskunft.
 */
export const KONTAKT = {
  email: 'portal@fairwaybid.com',
  telefon: null,
};

/** Vertretungsberechtigte Person(en) der GmbH. */
export const GESCHAEFTSFUEHRER = null;

/**
 * Handelsregister.
 *
 * Quelle: das Impressum der Vorstart-Seite auf www.fairwaybid.com, abgerufen
 * am 11.09.2026. Dort stehen sie als Angaben der Clubhouse Holding GmbH.
 * Nicht geraten und nicht aus dem alten Impressum der Vito's Sporting Goods
 * GmbH uebernommen - deren Nummer (HRB 10554) ist eine andere. Bitte einmal
 * gegen den Registerauszug pruefen.
 */
export const REGISTER = {
  gericht: 'Amtsgericht Friedberg',
  nummer: 'HRB 11068',
};

/** Umsatzsteuer-Identifikationsnummer nach § 27 a UStG. */
export const UST_ID = null;

/**
 * Was fehlt. Wird unten auf der Seite als Zeile ausgegeben, solange etwas
 * offen ist – damit niemand ein unvollstaendiges Impressum fuer vollstaendig
 * haelt, wir selbst eingeschlossen.
 */
export const fehlendeAngaben = () => {
  const offen = [];
  if (!GESCHAEFTSFUEHRER) offen.push('vertretungsberechtigte Person');
  if (!KONTAKT.telefon) offen.push('Telefonnummer');
  if (!REGISTER.gericht || !REGISTER.nummer) offen.push('Handelsregisterangaben');
  if (!UST_ID) offen.push('Umsatzsteuer-Identifikationsnummer');
  return offen;
};

/**
 * Ist das Impressum vollständig?
 *
 * Nicht nur für die Anzeige: Vor dem Livegang ist das die Frage, an der es
 * hängt. Ein unvollständiges Impressum auf einer gewerblichen Seite ist in
 * Deutschland abmahnfähig, und zwar unabhängig davon, wie fertig der Rest
 * der Plattform ist.
 */
export const impressumVollstaendig = () => fehlendeAngaben().length === 0;

/**
 * Die Zeilen der Angabenliste. Leere Werte fallen heraus, damit keine
 * Zeile mit einem Strich dasteht.
 */
export const angaben = () =>
  [
    { begriff: 'Anschrift', wert: [BETREIBER.strasse, BETREIBER.plzOrt, BETREIBER.land] },
    { begriff: 'Vertreten durch', wert: [GESCHAEFTSFUEHRER] },
    { begriff: 'E-Mail', wert: [KONTAKT.email], mailto: KONTAKT.email },
    { begriff: 'Telefon', wert: [KONTAKT.telefon] },
    { begriff: 'Registergericht', wert: [REGISTER.gericht] },
    { begriff: 'Registernummer', wert: [REGISTER.nummer] },
    { begriff: 'Umsatzsteuer-Identifikationsnummer', wert: [UST_ID] },
  ]
    .map(z => ({ ...z, wert: z.wert.filter(Boolean) }))
    .filter(z => z.wert.length > 0);

/**
 * Hinweis nach Art. 14 Abs. 1 ODR-Verordnung. Steht auf Deutsch, weil die
 * Seite deutsch ist - vorher stand hier ein englischer Absatz.
 */
export const ODR = {
  titel: 'Streitbeilegung',
  text:
    'Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung bereit. Wir sind nicht verpflichtet und nicht bereit, an einem Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.',
  linkText: 'ec.europa.eu/consumers/odr',
  linkUrl: 'https://ec.europa.eu/consumers/odr/',
};

/** Kurze Einordnung ganz oben - dieselbe Rolle wie die Zeile auf den Verkaufsseiten. */
export const EYEBROW = 'Rechtliche Angaben';
export const LEAD = 'Angaben gemäß § 5 Digitale-Dienste-Gesetz (DDG).';
